<template>
  <base-modal :show="showModal" @controleModalApperance="$emit('closeModal')">
    <template #modal>
      <div class="modal_card">
        <div class="modal_header">
          <h5>{{ $t("chatbotConversations.conversationDetails") }}</h5>
          <button class="close_btn" @click="$emit('closeModal')">
            <i class="fal fa-times"></i>
          </button>
        </div>

        <div class="modal_body">
          <div class="conversation_detail_wrapper" v-if="conversationData">
            <!-- Start:: Conversation Info -->
            <div class="info_section mb-4">
              <div class="row">
                <div class="col-md-6 mb-3">
                  <label class="d-block mb-1">{{
                    $t("chatbotConversations.conversationId")
                  }}</label>
                  <p class="info_text">#{{ conversationData.id }}</p>
                </div>
                <div class="col-md-6 mb-3">
                  <label class="d-block mb-1">{{
                    $t("chatbotConversations.userName")
                  }}</label>
                  <p class="info_text">
                    {{ conversationData.user?.name || "-" }}
                  </p>
                </div>
                <div class="col-md-6 mb-3">
                  <label class="d-block mb-1">{{
                    $t("chatbotConversations.messagesCount")
                  }}</label>
                  <p class="info_text">
                    {{ conversationData.messages_count || 0 }}
                  </p>
                </div>
              </div>
            </div>
            <!-- End:: Conversation Info -->

            <!-- Start:: Messages Section -->
            <div class="messages_section">
              <h6 class="section_title mb-3">
                {{ $t("chatbotConversations.messages") }}
              </h6>

              <!-- Messages List -->
              <div class="messages_container mb-3">
                <div
                  v-for="message in messages"
                  :key="message.id"
                  class="message_wrapper"
                  :class="message.sender"
                >
                  <div class="message_item">
                    <small class="message_sender_label">
                      {{ getSenderLabel(message.sender) }}
                    </small>
                    <div class="message_content">
                      <p class="message_text">{{ message.message }}</p>
                      <small class="message_time">{{
                        formatDate(message.created_at)
                      }}</small>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Reply Form -->
              <div class="reply_form">
                <div class="row">
                  <div class="col-md-9 mb-2">
                    <textarea
                      class="form-control"
                      rows="3"
                      :placeholder="
                        $t('chatbotConversations.messagePlaceholder')
                      "
                      v-model="replyMessage"
                    ></textarea>
                  </div>
                  <div class="col-md-3 mb-2 d-flex align-items-end">
                    <button
                      class="btn btn-primary w-100"
                      @click="sendReply"
                      :disabled="isSendingReply || !replyMessage"
                    >
                      {{ $t("chatbotConversations.sendMessage") }}
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <!-- End:: Messages Section -->
          </div>
        </div>
      </div>
    </template>
  </base-modal>
</template>

<script>
import BaseModal from "@/components/ui/BaseModal.vue";

export default {
  name: "ConversationDetail",

  components: {
    BaseModal,
  },

  props: {
    showModal: {
      type: Boolean,
      required: true,
    },
    conversationData: {
      type: Object,
      default: null,
    },
  },

  data() {
    return {
      // Start:: Messages Data
      messages: [],
      replyMessage: "",
      isSendingReply: false,
      realtimeChannelName: null,
      // End:: Messages Data
    };
  },

  watch: {
    showModal(newVal) {
      if (newVal) {
        this.startRealtimeListening();
      } else {
        this.stopRealtimeListening();
      }
    },

    conversationData: {
      handler(newVal) {
        if (newVal) {
          this.getMessages();
          if (this.showModal) {
            this.startRealtimeListening();
          }
        } else {
          this.stopRealtimeListening();
        }
      },
      immediate: true,
      deep: true,
    },
  },

  methods: {
    // Start:: Normalize Incoming Messages
    normalizeIncomingMessages(payload) {
      if (!payload) return [];

      if (Array.isArray(payload)) return payload;

      const data = payload.data || payload;

      if (Array.isArray(data.messages)) return data.messages;
      if (data.message && typeof data.message === "object")
        return [data.message];

      if (
        data.id &&
        Object.prototype.hasOwnProperty.call(data, "sender") &&
        Object.prototype.hasOwnProperty.call(data, "message")
      ) {
        return [data];
      }

      return [];
    },
    // End:: Normalize Incoming Messages

    // Start:: Start Realtime Listening
    startRealtimeListening() {
      const conversationId = this.conversationData?.id;

      if (!this.showModal || !conversationId || !window.Echo) {

        return;
      }

      const channelName = `chatbot.${conversationId}`;
      if (this.realtimeChannelName === channelName) {
        return;
      }

      this.stopRealtimeListening();
      this.realtimeChannelName = channelName;

      const channel = window.Echo.channel(channelName);

      if (typeof channel.subscribed === "function") {
        channel.subscribed(() => {

        });
      }

      if (typeof channel.error === "function") {
        channel.error((error) => {
          console.error("[ChatbotRealtime] Subscription error", {
            channelName,
            error,
          });
        });
      }

      const handleIncomingEvent = (event) => {
        const incomingMessages = this.normalizeIncomingMessages(event);
        if (!incomingMessages.length) {
          console.warn("[ChatbotRealtime] No valid messages found in payload", {
            channelName,
          });
          return;
        }

        incomingMessages.forEach((incomingMessage) => {
          const alreadyExists = this.messages.some((message) => {
            if (message.id && incomingMessage.id) {
              return message.id === incomingMessage.id;
            }

            return (
              message.created_at === incomingMessage.created_at &&
              message.sender === incomingMessage.sender &&
              message.message === incomingMessage.message
            );
          });

          if (!alreadyExists) {
            this.messages.push(incomingMessage);
          } else {
          }
        });
      };

      channel
        .listen(".send.msg_chatBot", handleIncomingEvent)
        .listen("send.msg_chatBot", handleIncomingEvent);
    },
    // End:: Start Realtime Listening

    // Start:: Stop Realtime Listening
    stopRealtimeListening() {
      if (window.Echo && this.realtimeChannelName) {
        window.Echo.leave(this.realtimeChannelName);
      }

      this.realtimeChannelName = null;
    },
    // End:: Stop Realtime Listening

    // Start:: Get Messages
    async getMessages() {
      if (!this.conversationData) return;

      try {
        let res = await this.$axios({
          method: "GET",
          url: `chatbots/${this.conversationData.id}`,
        });
        this.messages = res.data.data?.conversation.messages || [];
      } catch (error) {
        console.log(error.response?.data?.message);
      }
    },
    // End:: Get Messages

    // Start:: Get Sender Label
    getSenderLabel(sender) {
      const labels = {
        user: "المستخدم",
        bot: "البوت",
        admin: "الإدارة",
      };
      return labels[sender] || sender;
    },
    // End:: Get Sender Label

    // Start:: Format Date
    formatDate(dateStr) {
      if (!dateStr) return "";
      const date = new Date(dateStr);
      return date.toLocaleString("ar-EG");
    },
    // End:: Format Date

    // Start:: Send Reply
    async sendReply() {
      if (!this.replyMessage.trim()) return;

      this.isSendingReply = true;
      try {
        await this.$axios({
          method: "POST",
          url: `chatbots/${this.conversationData.id}/reply`,
          data: {
            message: this.replyMessage,
          },
        });

        this.$message.success(this.$t("chatbotConversations.replySuccess"));
        this.replyMessage = "";
        this.getMessages();
        this.$emit("refreshData");
      } catch (error) {
        this.$message.error(
          error.response?.data?.message || this.$t("errorOccurred")
        );
      } finally {
        this.isSendingReply = false;
      }
    },
    // End:: Send Reply
  },

  beforeDestroy() {
    this.stopRealtimeListening();
  },
};
</script>

<style scoped lang="scss">
.modal_card {
  background: white;
  border-radius: 12px;
  max-width: 900px;
  width: 90vw;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;

  .modal_header {
    padding: 20px 25px;
    border-bottom: 1px solid #eee;
    display: flex;
    justify-content: space-between;
    align-items: center;

    h5 {
      margin: 0;
      font-size: 18px;
      font-weight: 600;
    }

    .close_btn {
      background: none;
      border: none;
      font-size: 20px;
      cursor: pointer;
      color: #999;
      padding: 5px 10px;

      &:hover {
        color: #333;
      }
    }
  }

  .modal_body {
    overflow-y: auto;
    flex: 1;
  }
}

.conversation_detail_wrapper {
  padding: 20px;

  .info_section {
    border-bottom: 1px solid #eee;
    padding-bottom: 20px;

    label {
      font-size: 13px;
      font-weight: 600;
      color: #666;
    }

    .info_text {
      font-size: 14px;
      color: #333;
      margin: 0;
    }
  }

  .messages_section {
    .section_title {
      font-size: 16px;
      font-weight: 600;
      color: #333;
    }

    .messages_container {
      max-height: 400px;
      overflow-y: auto;
      border: 1px solid #eee;
      border-radius: 8px;
      padding: 15px;
      background: #f9f9f9;
      display: flex;
      flex-direction: column;
      gap: 12px;

      .message_wrapper {
        display: flex;
        margin-bottom: 8px;

        &.user {
          justify-content: flex-end;

          .message_item {
            background: #007bff;
            color: white;
            max-width: 70%;
            border-radius: 12px 12px 4px 12px;

            .message_sender_label {
              color: rgba(255, 255, 255, 0.8);
              font-weight: 600;
              display: block;
              margin-bottom: 4px;
              font-size: 11px;
            }

            .message_text {
              color: white;
            }

            .message_time {
              color: rgba(255, 255, 255, 0.7);
            }
          }
        }

        &.bot,
        &.admin {
          justify-content: flex-start;

          .message_item {
            background: white;
            border: 1px solid #ddd;
            max-width: 70%;
            border-radius: 12px 12px 12px 4px;

            .message_sender_label {
              color: #666;
              font-weight: 600;
              display: block;
              margin-bottom: 4px;
              font-size: 11px;
            }

            .message_text {
              color: #333;
            }

            .message_time {
              color: #999;
            }
          }
        }

        &.admin {
          .message_item {
            background: #e8f5e9;
            border: 1px solid #c8e6c9;

            .message_sender_label {
              color: #2e7d32;
            }

            .message_text {
              color: #1b5e20;
            }
          }
        }

        .message_item {
          padding: 10px 12px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

          .message_content {
            display: flex;
            flex-direction: column;
            gap: 4px;

            .message_sender_label {
              margin: 0;
              font-size: 11px;
            }

            .message_text {
              margin: 0;
              font-size: 14px;
              word-wrap: break-word;
              word-break: break-word;
            }

            .message_time {
              margin: 0;
              font-size: 11px;
              margin-top: 4px;
            }
          }
        }
      }
    }

    .reply_form {
      margin-top: 20px;

      textarea {
        resize: none;
        border: 1px solid #ddd;
        border-radius: 6px;
        padding: 10px;
        font-family: inherit;

        &:focus {
          outline: none;
          border-color: #007bff;
          box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
        }
      }

      button {
        border-radius: 6px;
        font-weight: 600;
        transition: all 0.3s ease;

        &:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
        }

        &:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
      }
    }
  }
}
</style>
